/**
 * AdoraCare - Case Sheet V1 DOCTOR cockpit view model
 * (SANDBOX-CASE-SHEET-WORKBENCH-V1, package 2B).
 *
 * Pure, React-free helpers + types + the back-half FINALIZE orchestration for
 * the doctor cockpit. Kept side-effect-free (no React, no CSS) so the vitest
 * suite exercises every derivation / mapping / orchestration path by direct unit
 * call and renders the presentational component via react-dom/server (this repo
 * has NO jsdom). Mirrors clinical-workbench-view.ts.
 *
 * HARD DATA RULE (Manoj's hardest line). Every value surfaced here maps to a
 * real #181-persisted column of the DOCTOR clinical view (api.ts
 * CaseSheetClinicalView / ClinicalPlanItemView). The view carries NO patient
 * name, NO clinic name, NO appointment time, NO clinicId, so the cockpit shows
 * only the ids + chiefComplaint + status + persisted timestamps it actually
 * receives - it never fabricates a display field the backend cannot return.
 *
 * THE "SYSTEM DERIVES" HALF (the ONE part with real logic). finalizeVisit closes
 * the 2A gap: it persists the case sheet (updateCaseSheet status=finalized) AND
 * records ONE consultation-level outcome via the EXISTING back-half path
 * (recordOutcome, reused verbatim from clinical-workbench-view). The follow-up
 * fires ONCE PER CONSULTATION (one recordOutcome call), NEVER once per plan item
 * - even when several plan items are declined. The visit outcome is the doctor's
 * EXPLICIT choice (suggestVisitOutcome pre-fills a suggestion from the plan; the
 * doctor confirms/overrides), so exactly one consultation outcome is recorded
 * regardless of how many line items are declined.
 */

import type {
  CaseSheetClinicalView,
  CaseSheetUpdateInput,
  ClinicalPlanItemView,
  PlanItemCreateInput,
  PlanItemStatusView,
} from '@/dashboard/api';
import {
  recordOutcome,
  type OutcomeAction,
  type OutcomeRecordDeps,
  type WorkbenchFeedback,
} from '@/dashboard/clinical-workbench/clinical-workbench-view';
import {
  CASE_SHEET_LABELS,
  caseSheetStatusLabel,
  planItemStatusLabel,
} from '@/dashboard/clinical-workbench/case-sheet-labels';

// --- Money (whole INR, matching the repo-wide en-IN pattern) ---------------

/** The Indian Rupee sign (U+20B9), built via char code so this source file
 *  stays ASCII-clean while rendering the same glyph the dashboards use inline. */
const RUPEE_SIGN = String.fromCharCode(0x20b9);

/**
 * Format a whole-INR estimate for display. estimated_value is BIGINT whole
 * rupees (the money-of-record); a null estimate (a draft item) renders as
 * "not set". Mirrors the inline rupee + en-IN grouping pattern used across the
 * owner / doctor dashboards.
 */
export function formatWholeInr(value: number | null): string {
  if (value === null || !Number.isFinite(value)) return 'not set';
  return `${RUPEE_SIGN}${value.toLocaleString('en-IN')}`;
}

/**
 * Parse an operator-typed whole-rupee estimate into a non-negative integer, or
 * null when blank (an honest "no estimate"), or `false` when malformed (non-
 * numeric / negative / fractional) so the caller can block the write pre-API.
 * Accepts a leading currency symbol / commas / spaces.
 */
export function parseWholeInr(raw: string): number | null | false {
  const trimmed = raw
    .trim()
    .replace(new RegExp(`^${RUPEE_SIGN}`), '')
    .replace(/,/g, '')
    .trim();
  if (trimmed === '') return null;
  if (!/^\d+$/.test(trimmed)) return false;
  const n = Number(trimmed);
  return Number.isInteger(n) && n >= 0 ? n : false;
}

/**
 * DOCTOR-PRICING-UX-CONFORMANCE-V1: reformat a typed whole-rupee estimate
 * once the doctor leaves the field (blur), so a six-figure amount reads with
 * Indian grouping instead of a bare integer -- a misread risk on a number
 * entering a patient's quote. Composes the SAME parseWholeInr/formatWholeInr
 * pair verbatim (never a second formatter/parser): blank or malformed input
 * comes back UNCHANGED (an incomplete or invalid keystroke is never
 * mangled); a parseable amount round-trips through formatWholeInr, so
 * re-parsing the formatted string yields the identical integer.
 */
export function estimateInputDisplayOnBlur(raw: string): string {
  const parsed = parseWholeInr(raw);
  return typeof parsed === 'number' ? formatWholeInr(parsed) : raw;
}

// --- treatment price floor (TREATMENT-PRICE-FLOOR-V1, founder ruling 2026-08-26) --

/**
 * Mirror of the backend's ONLY canonical comparison
 * (lib/domain/clinical/treatment-catalog.ts validateTreatmentQuoteAgainstFloor)
 * for INSTANT field feedback AND the pre-API submit gate below -- UI
 * validation is feedback, never enforcement; the backend write path is the
 * authoritative boundary and re-checks independently on every write. The
 * frontend cannot import backend code (the repo's established mirror
 * pattern), so this is a SEPARATE implementation kept identical BY TEST, not
 * by import. Lives here (not treatment-picker-view.ts) so BOTH the picker's
 * live field display and this module's own buildPlanItemCreatePayload gate
 * call the SAME one function -- treatment-picker-view.ts already imports
 * FROM this module (formatWholeInr), so the reverse import would cycle.
 *
 * Returns null when the amount is valid (including: no floor configured, or
 * the amount is not yet a parseable whole number -- an incomplete keystroke
 * is not an error). INTEGER ARITHMETIC ONLY: the whole-rupee proposal is
 * converted to minor units by MULTIPLYING by 100 (exact, lossless) and
 * compared directly against the stored minor-units floor -- never divide the
 * floor by 100 (that would truncate a non-round floor and silently weaken
 * it).
 */
export function treatmentFloorViolationMessage(
  minimumAmountMinorUnits: number | null | undefined,
  proposedAmountInr: number | null,
  treatmentName: string,
): string | null {
  if (
    minimumAmountMinorUnits === null ||
    minimumAmountMinorUnits === undefined ||
    !Number.isInteger(minimumAmountMinorUnits) ||
    minimumAmountMinorUnits < 0
  ) {
    return null;
  }
  if (proposedAmountInr === null || !Number.isInteger(proposedAmountInr)) {
    return null;
  }
  const proposedAmountMinorUnits = proposedAmountInr * 100;
  if (proposedAmountMinorUnits >= minimumAmountMinorUnits) return null;
  // THE EXACT FLOOR AMOUNT IS NEVER RENDERED in this message
  // (DOCTOR-FLOOR-DISCLOSURE-V1, founder ruling 2026-08-28, superseding the
  // 2026-08-26 wording): a patient sits beside the chairside screen, and
  // interpolating the minimum hands them the number and the knowledge that
  // a lower price was attempted. The comparison above still uses the exact
  // stored floor; only the rendered wording is disclosure-safe. Kept
  // literal-identical to the backend renderer BY TEST, never by import.
  return `${treatmentName}: amount is below the permitted minimum.`;
}

// --- STATUS timeline (the journey breadcrumb) ------------------------------

/** One step in the Lead -> Appointment -> Visit -> Treatment breadcrumb. */
export interface JourneyStep {
  readonly key: string;
  readonly label: string;
  /** 'done' (a persisted fact backs it), 'current' (the active step), or
   *  'todo' (not yet reached / no backing data). Display only. */
  readonly state: 'done' | 'current' | 'todo';
  /** A short, PHI-free detail (an id, a count, a status token). */
  readonly detail: string;
}

/** Categorical roll-up of a plan (counts by line-item status). Pure.
 *  'deferred' (20260714_2): the patient EXPLICITLY postponed the item -- a
 *  real recorded decision, counted separately (never folded into pending). */
export interface PlanRollup {
  readonly total: number;
  readonly advised: number;
  readonly accepted: number;
  readonly declined: number;
  readonly pending: number;
  readonly deferred: number;
  readonly estimatedTotal: number;
}

/** Roll a plan's line items into categorical counts + the INR estimate total. */
export function rollupPlan(items: readonly ClinicalPlanItemView[]): PlanRollup {
  let advised = 0;
  let accepted = 0;
  let declined = 0;
  let pending = 0;
  let deferred = 0;
  let estimatedTotal = 0;
  for (const i of items) {
    if (i.status === 'advised') advised += 1;
    else if (i.status === 'accepted') accepted += 1;
    else if (i.status === 'declined') declined += 1;
    else if (i.status === 'pending') pending += 1;
    else if (i.status === 'deferred') deferred += 1;
    if (typeof i.estimatedValue === 'number') estimatedTotal += i.estimatedValue;
  }
  return { total: items.length, advised, accepted, declined, pending, deferred, estimatedTotal };
}

/**
 * Build the status timeline for a loaded (or not-yet-created) case sheet. Every
 * step's state + detail is derived STRICTLY from persisted #181 data: whether a
 * lead is linked, the appointment id, the visit (case-sheet) status, and the
 * plan roll-up. `sheet === null` = the visit is opened but no case sheet exists
 * yet (the "start notes" state).
 */
export function buildJourney(
  appointmentId: number,
  sheet: CaseSheetClinicalView | null,
): readonly JourneyStep[] {
  const leadLinked = sheet !== null && sheet.leadId !== null;
  const roll = sheet ? rollupPlan(sheet.planItems) : null;
  const hasOutcome =
    roll !== null && (roll.advised + roll.accepted + roll.declined > 0);

  return [
    {
      key: 'lead',
      label: 'Lead',
      state: leadLinked ? 'done' : 'todo',
      detail:
        sheet === null
          ? 'n/a'
          : sheet.leadId !== null
            ? `#${sheet.leadId}`
            : 'not linked',
    },
    {
      key: 'appointment',
      label: 'Appointment',
      state: 'done',
      detail: `#${appointmentId}`,
    },
    {
      key: 'visit',
      label: CASE_SHEET_LABELS.caseSheet,
      state: sheet === null ? 'current' : sheet.status === 'finalized' ? 'done' : 'current',
      detail: sheet === null ? 'not started' : caseSheetStatusLabel(sheet.status),
    },
    {
      key: 'treatment',
      label: 'Treatment',
      state: hasOutcome ? 'done' : sheet !== null && roll!.total > 0 ? 'current' : 'todo',
      detail:
        roll === null || roll.total === 0
          ? 'no procedures'
          : `${roll.total} total, ${roll.accepted} accepted, ${roll.declined} declined`,
    },
  ];
}

// --- NEXT-ACTION bar (suggestive, never forceful) --------------------------

/**
 * Suggest the next action(s) for the doctor - GUIDANCE only (the cockpit
 * presents these as "Suggested next", never as a forced step). Derived from the
 * persisted state: no sheet -> start notes; draft + no items -> add procedures;
 * a declined+recoverable plan -> hint the recovery follow-up; draft -> finalize
 * when ready; finalized -> record the consultation outcome if not yet done.
 */
export function suggestNextActions(
  sheet: CaseSheetClinicalView | null,
): readonly string[] {
  if (sheet === null) {
    return [`Start the ${CASE_SHEET_LABELS.caseSheet.toLowerCase()} for this visit.`];
  }
  const roll = rollupPlan(sheet.planItems);
  const out: string[] = [];
  if (sheet.status === 'draft') {
    if (roll.total === 0) {
      out.push(`Add the ${CASE_SHEET_LABELS.treatmentPlanItems.toLowerCase()} the doctor advised.`);
    }
    if (hasRecoverableDecline(sheet.planItems)) {
      out.push('A declined procedure has a recoverable reason - finalizing can queue a price/timing follow-up for COC.');
    }
    out.push('Finalize the visit when the notes and plan are complete.');
  } else {
    out.push('Visit is finalized. Record the consultation outcome if you have not already, so the follow-up loop can run.');
  }
  return out;
}

// --- VISIT OUTCOME derivation (the consultation-level, one-per-visit fact) --

/** The doctor's explicit consultation-outcome selection at finalize. `action`
 *  reuses the back-half OutcomeAction union; declineReason is '' unless the
 *  action is treatment_declined (then '' = no reason, or a controlled token). */
export interface VisitOutcomeSelection {
  readonly action: OutcomeAction;
  readonly declineReason: string;
}

/** The recoverable controlled reasons (mirror of the backend
 *  RECOVERABLE_OUTCOME_REASONS; same set the categorical workbench mirrors). */
export const RECOVERABLE_DECLINE_TOKENS: ReadonlySet<string> = new Set<string>([
  'price',
  'timing',
  'no_response',
]);

/** The highest-priority (lowest priorityRank) declined item carrying a
 *  RECOVERABLE reason, or null. Deterministic tie-break by priorityRank then
 *  id, so the "primary" declined procedure is stable. */
export function primaryRecoverableDecline(
  items: readonly ClinicalPlanItemView[],
): ClinicalPlanItemView | null {
  let best: ClinicalPlanItemView | null = null;
  for (const i of items) {
    if (i.status !== 'declined') continue;
    if (i.declineReason === null || !RECOVERABLE_DECLINE_TOKENS.has(i.declineReason)) {
      continue;
    }
    if (
      best === null ||
      i.priorityRank < best.priorityRank ||
      (i.priorityRank === best.priorityRank && i.id < best.id)
    ) {
      best = i;
    }
  }
  return best;
}

/** True iff the plan has at least one declined item with a recoverable reason. */
export function hasRecoverableDecline(
  items: readonly ClinicalPlanItemView[],
): boolean {
  return primaryRecoverableDecline(items) !== null;
}

/**
 * SUGGEST the consultation-level visit outcome from the plan (a suggestion the
 * doctor confirms/overrides at finalize - it is NEVER auto-applied). ONE
 * outcome regardless of how many items are declined:
 *   - a declined+recoverable plan -> declined with the PRIMARY recoverable
 *     reason (so the recovery follow-up can fire once);
 *   - else any accepted item -> accepted;
 *   - else any advised item -> advised;
 *   - else null (e.g. all pending - nothing to record yet).
 * This is the package's "explicit doctor-chosen visit outcome, seeded from the
 * primary declined item" derivation - it collapses N declined items to ONE.
 */
export function suggestVisitOutcome(
  items: readonly ClinicalPlanItemView[],
): VisitOutcomeSelection | null {
  const primaryDecline = primaryRecoverableDecline(items);
  if (primaryDecline !== null) {
    return {
      action: 'treatment_declined',
      declineReason: primaryDecline.declineReason ?? '',
    };
  }
  const roll = rollupPlan(items);
  if (roll.accepted > 0) return { action: 'treatment_accepted', declineReason: '' };
  if (roll.advised > 0) return { action: 'treatment_advised', declineReason: '' };
  // A plan that has only declined-but-NON-recoverable items still records a
  // (non-recoverable) declined outcome; that is an honest visit outcome that
  // simply spawns no follow-up server-side.
  if (roll.declined > 0) {
    const anyDeclined = items.find((i) => i.status === 'declined');
    return {
      action: 'treatment_declined',
      declineReason: anyDeclined?.declineReason ?? '',
    };
  }
  return null;
}

// --- FINALIZE orchestration (persist case sheet + record ONE outcome) -------

/** The two injected async capabilities finalizeVisit needs: the #181 case-sheet
 *  update, and the EXISTING back-half outcome recorder deps (reused verbatim). */
export interface FinalizeVisitDeps {
  readonly updateCaseSheet: (
    caseSheetId: number,
    patch: CaseSheetUpdateInput,
  ) => Promise<CaseSheetClinicalView>;
  /** The existing markTreatment* / advanceLeadStageBackHalf client fns that
   *  clinical-workbench-view's recordOutcome drives. */
  readonly outcome: OutcomeRecordDeps;
}

export interface FinalizeVisitInput {
  readonly caseSheetId: number;
  readonly appointmentId: number;
  /** The ONE consultation-level outcome to record, or null to finalize the case
   *  sheet WITHOUT recording a back-half outcome. */
  readonly visitOutcome: VisitOutcomeSelection | null;
  /** Pre-resolved display label for a decline reason (the caller owns the map). */
  readonly declineReasonLabel?: string;
}

export interface FinalizeVisitResult {
  /** The finalized case sheet (null only when the finalize write itself failed). */
  readonly sheet: CaseSheetClinicalView | null;
  readonly feedback: WorkbenchFeedback;
}

/**
 * Close the loop: persist the case-sheet FINALIZE via #181, then - if the doctor
 * chose a visit outcome - record ONE consultation-level outcome via the EXISTING
 * back-half recordOutcome path (which drives markTreatment* + the follow-up hook
 * server-side). Order matters:
 *   1. FINALIZE the case sheet (the cockpit's primary #181 artifact). If this
 *      fails, STOP and report - do NOT record a back-half outcome for a visit
 *      that did not finalize.
 *   2. If a visit outcome is chosen, call recordOutcome EXACTLY ONCE. This is
 *      the cardinality guarantee: one finalize -> one recordOutcome ->
 *      (for a recoverable decline) one markTreatmentDeclined -> one follow-up,
 *      no matter how many plan items are declined. advancePatientStage is false
 *      because the DOCTOR never owns the coc-only `patient` stage (STAGE_CONTRACTS);
 *      recordOutcome reports that plainly instead of firing a doomed request.
 * A partial failure (case sheet finalized, but the back-half outcome errored -
 * e.g. the appointment is not fulfilled -> 409) is SURFACED honestly: the sheet
 * is returned finalized and the feedback carries both the finalize line and the
 * outcome error. Never throws (recordOutcome already converts every rejection to
 * feedback; the finalize write is the only throwing call and is caught).
 */
export async function finalizeVisit(
  deps: FinalizeVisitDeps,
  input: FinalizeVisitInput,
): Promise<FinalizeVisitResult> {
  let sheet: CaseSheetClinicalView;
  try {
    sheet = await deps.updateCaseSheet(input.caseSheetId, { status: 'finalized' });
  } catch (err) {
    return {
      sheet: null,
      feedback: {
        kind: 'error',
        lines: [finalizeErrorLine(err)],
      },
    };
  }

  const finalizeLine = `${CASE_SHEET_LABELS.caseSheet} finalized for appointment #${input.appointmentId}.`;

  if (input.visitOutcome === null) {
    return { sheet, feedback: { kind: 'success', lines: [finalizeLine] } };
  }

  // ONE consultation-level outcome via the EXISTING back-half orchestrator. This
  // is the single call that (for a recoverable decline) creates the ONE
  // follow-up - never once per declined line item.
  const out = await recordOutcome(deps.outcome, {
    action: input.visitOutcome.action,
    appointmentId: input.appointmentId,
    declineReason:
      input.visitOutcome.action === 'treatment_declined'
        ? input.visitOutcome.declineReason
        : '',
    declineReasonLabel: input.declineReasonLabel ?? '',
    // The doctor records the outcome but does NOT advance the coc-only patient
    // stage; recordOutcome reports "ask COC to advance" instead of a doomed call.
    advancePatientStage: false,
  });

  return {
    sheet,
    feedback: {
      kind: out.feedback.kind,
      lines: [finalizeLine, ...out.feedback.lines],
    },
  };
}

/** A PHI-free message for a failed case-sheet write. Branches on the stable
 *  machine code the backend carries (PortalApiError), else the message. */
function finalizeErrorLine(err: unknown): string {
  const code =
    err !== null && typeof err === 'object' && 'code' in err
      ? (err as { code?: unknown }).code
      : null;
  if (code === 'case_sheet_exists') {
    return 'A visit note already exists for this appointment. Reopen the visit to edit it.';
  }
  if (err instanceof Error && err.message) return `Could not finalize the visit: ${err.message}`;
  return 'Could not finalize the visit. Please try again.';
}

// --- Plan-item edit helpers -------------------------------------------------

/** The subset of plan-item fields the inline row editor changes: the outcome
 *  status, (only when declined) the controlled decline reason, and the
 *  doctor's per-line remark. */
export interface PlanItemEdit {
  readonly status: PlanItemStatusView;
  /** '' = clear the reason; a controlled OUTCOME_REASONS token otherwise. */
  readonly declineReason: string;
  /** DOCTOR-CONSENT-REMARKS-V1: the per-line remark as typed. '' = the
   *  doctor cleared it. UNDEFINED means this edit is not about the remark at
   *  all (the inline Accept/Decline/Defer act builds such an edit), and the
   *  patch below then omits the key so a SAVED remark is never blanked by a
   *  decision tap. */
  readonly itemNote?: string;
}

/**
 * Normalise an inline plan-item edit into a #181 PUT patch, enforcing the DB
 * presence rule client-side (decline_reason is meaningful ONLY when status =
 * declined): a non-declined status always clears the reason to null, so the
 * backend presence CHECK can never reject the write. Returns the patch to send.
 *
 * The remark follows the SERVER's nullable-text convention exactly: the key is
 * present only when this edit carries one, '' becomes null (an explicit
 * clear), and text travels trimmed. An absent key leaves the stored remark
 * untouched -- MISSING PRESERVES, which is what makes a decision tap safe.
 */
export function planItemEditPatch(edit: PlanItemEdit): {
  status: PlanItemStatusView;
  declineReason: string | null;
  itemNote?: string | null;
} {
  const note =
    edit.itemNote === undefined
      ? {}
      : { itemNote: edit.itemNote.trim() === '' ? null : edit.itemNote.trim() };
  if (edit.status !== 'declined') {
    return { status: edit.status, declineReason: null, ...note };
  }
  return {
    status: 'declined',
    declineReason: edit.declineReason === '' ? null : edit.declineReason,
    ...note,
  };
}

/** A one-line, PHI-free summary of a plan item for the list row (label-driven). */
export function planItemSummary(item: ClinicalPlanItemView): string {
  const parts = [item.procedureName];
  if (item.toothArea !== null && item.toothArea !== '') {
    parts.push(`(${item.toothArea})`);
  }
  parts.push(formatWholeInr(item.estimatedValue));
  parts.push(planItemStatusLabel(item.status));
  return parts.join(' - ');
}

// --- form -> #181 payload builders (pure; extracted for unit coverage) ------

/** The notes-form fields the cockpit edits (structural mirror of the component
 *  NotesForm; kept here so the payload transform is unit-testable). */
export interface CaseSheetNotesInput {
  readonly chiefComplaint: string;
  readonly findingsDiagnosis: string;
  readonly clinicalNote: string;
}

/**
 * Map the notes form to a #181 case-sheet patch: a blank / whitespace-only field
 * becomes null (the honest "not captured" value the backend stores), a filled
 * field is trimmed. Pure - the single source of truth for the create/update
 * notes shape the cockpit sends.
 */
export function caseSheetNotePatch(notes: CaseSheetNotesInput): {
  chiefComplaint: string | null;
  findingsDiagnosis: string | null;
  clinicalNote: string | null;
} {
  const toValue = (raw: string): string | null => {
    const t = raw.trim();
    return t === '' ? null : t;
  };
  return {
    chiefComplaint: toValue(notes.chiefComplaint),
    findingsDiagnosis: toValue(notes.findingsDiagnosis),
    clinicalNote: toValue(notes.clinicalNote),
  };
}

/** The add-procedure form fields (structural mirror of the component ItemForm).
 *  CODED-PICKER mode (V6-P3): a non-null catalogItemId means the doctor picked
 *  a catalog treatment -- the server resolves the label from the catalog, so
 *  procedureName is not required (catalogLabel is display-only). null =
 *  free-text mode, exactly today's shape. */
export interface PlanItemFormInput {
  readonly procedureName: string;
  readonly catalogItemId?: number | null;
  /** Display-only echo of the picked catalog name (never sent). */
  readonly catalogLabel?: string;
  /** Display-only echo of the picked item's configured floor
   *  (TREATMENT-PRICE-FLOOR-V1; never sent -- the backend re-checks the
   *  LIVE catalog value independently). Absent/null = no floor to gate on
   *  (legacy free-text mode, or a coded item with none configured). */
  readonly catalogMinimumMinorUnits?: number | null;
  readonly toothArea: string;
  /** Item-grain canonical tooth codes from the odontogram
   *  (TOOTH-NOTATION-INTERIM-V1). OPTIONAL structural mirror: absent/empty
   *  keeps the payload byte-identical to today (the toothRefs key never
   *  rides an empty selection). */
  readonly toothRefs?: readonly string[];
  readonly estimatedValue: string;
  readonly status: PlanItemStatusView;
  readonly declineReason: string;
}

/** Validated add-item result: a ready #181 create payload, or a pre-API error. */
export type PlanItemCreateResult =
  | { readonly ok: true; readonly payload: PlanItemCreateInput }
  | { readonly ok: false; readonly error: string };

/**
 * Validate + assemble the add-procedure form into a #181 create payload, or a
 * pre-API error (an honest gate BEFORE any network call, never a fake save).
 * Enforces: procedure name required (free-text mode) OR a picked catalog item
 * (coded mode -- the server writes the catalog name as the label); estimate is
 * a whole-rupee amount or blank; `declineReason` is included ONLY when
 * status===declined (the DB presence rule, so the backend CHECK can never
 * reject the write). Pure - no network, no throw.
 */
export function buildPlanItemCreatePayload(
  form: PlanItemFormInput,
): PlanItemCreateResult {
  const catalogItemId = form.catalogItemId ?? null;
  const procedureName = form.procedureName.trim();
  if (catalogItemId === null && procedureName === '') {
    return { ok: false, error: 'Enter a procedure name.' };
  }
  const est = parseWholeInr(form.estimatedValue);
  if (est === false) {
    return { ok: false, error: 'Estimated cost must be a whole rupee amount.' };
  }
  // TREATMENT-PRICE-FLOOR-V1: an honest pre-API gate mirroring the SAME
  // backend rule (never a second implementation -- see
  // treatmentFloorViolationMessage above). The backend re-checks
  // independently regardless; this only saves a doomed round-trip.
  const floorViolation = treatmentFloorViolationMessage(
    form.catalogMinimumMinorUnits,
    est,
    form.catalogLabel ?? '',
  );
  if (floorViolation !== null) {
    return { ok: false, error: floorViolation };
  }
  const declineReason =
    form.status === 'declined' && form.declineReason !== '' ? form.declineReason : null;
  const toothRefs = form.toothRefs ?? [];
  const base = {
    toothArea: form.toothArea.trim() === '' ? null : form.toothArea.trim(),
    // Structured tooth codes ride ONLY a non-empty selection -- an empty one
    // keeps the payload byte-identical to today (the server treats an absent
    // key as "tooth tables never named"; validation is server-side too).
    ...(toothRefs.length > 0 ? { toothRefs: [...toothRefs] } : {}),
    estimatedValue: est,
    status: form.status,
    declineReason,
  };
  return {
    ok: true,
    payload:
      catalogItemId !== null
        ? { catalogItemId, ...base }
        : { procedureName, ...base },
  };
}

/**
 * Merge a fresh server plan into the in-progress inline-edit map: seed a fresh
 * edit from the server for a NEW row, DROP entries for removed rows, and for a
 * still-present row RECONCILE the doctor's pending edit against the server.
 *
 * THE RECONCILIATION IS A THREE-WAY MERGE, and `previous` is what makes it
 * possible: for each field, a pending value that still EQUALS what the server
 * last said is one the doctor never touched, so it adopts the server's new
 * value; a pending value that DIFFERS is a live edit and is kept.
 *
 * Why it must be three-way. The inline Accept/Decline/Defer tap and the row
 * editor write to the SAME item. Keeping every pending field (the old
 * behaviour) meant that after a decision landed on the server the editor still
 * held the PRE-decision status, and the doctor's next save -- adding a remark,
 * say -- carried that stale status back and silently reverted the acceptance.
 * Adopting every server field instead would discard whatever the doctor was
 * part-way through typing. Only the merge is safe in both directions.
 *
 * `previous` is OPTIONAL: with no baseline there is nothing to prove a field
 * untouched, so every pending edit is kept exactly as before (the initial
 * seed, and any caller that has no prior plan in hand). Pure.
 */
export function syncItemEdits(
  prev: Readonly<Record<number, PlanItemEdit>>,
  items: readonly ClinicalPlanItemView[],
  previous?: readonly ClinicalPlanItemView[],
): Record<number, PlanItemEdit> {
  const before = new Map<number, ClinicalPlanItemView>();
  for (const it of previous ?? []) before.set(it.id, it);

  const next: Record<number, PlanItemEdit> = {};
  for (const it of items) {
    const pending = prev[it.id];
    if (pending === undefined) {
      next[it.id] = {
        status: it.status,
        declineReason: it.declineReason ?? '',
        itemNote: it.itemNote ?? '',
      };
      continue;
    }
    const was = before.get(it.id);
    if (was === undefined) {
      next[it.id] = pending;
      continue;
    }
    // Untouched === still equal to what the server last said -> adopt.
    next[it.id] = {
      status: pending.status === was.status ? it.status : pending.status,
      declineReason:
        pending.declineReason === (was.declineReason ?? '')
          ? (it.declineReason ?? '')
          : pending.declineReason,
      itemNote:
        pending.itemNote === undefined
          ? (it.itemNote ?? '')
          : pending.itemNote === (was.itemNote ?? '')
            ? (it.itemNote ?? '')
            : pending.itemNote,
    };
  }
  return next;
}
